import { Flex, HStack, Stack, VStack } from "@chakra-ui/layout";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import LoadingIndicator from "../components/Loading";
import {
	useCustomerUsersCustomers,
	useJobSchedule,
	useOrganisation,
	useScheduleActions,
	useScheduleJobApplications,
	useScheduleJobDetails,
} from "@api/hooks";
import { fetchJobMatchingConsultants } from "@api/hooks/useSchedule";
import {
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Checkbox,
	CheckboxGroup,
	Text,
	useToast,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import DropDownIcon from "../components/icons/DropDownIcon";
import NewJobButton from "../components/jobList/NewJobButton";
import WeekSchedule, { WeekScheduleHandle } from "../components/scheduleView/week/WeekSchedule";
import type { JobSchedule } from "@models/Schedule";
import { getCompetenceLabelByKey } from "@utils/utils";

/** Weeks around the current week that are loaded first */
const RANGE_PAD_BEFORE = 2;
const RANGE_PAD_AFTER = 12;
/** Weeks loaded at a time when scrolling close to the first or last week */
const RANGE_EXTEND_BY = 8;
/** Planning happens forward, so scrolling back stops two months before the
 * current week; forward it stops after two years like the dashboard's planner */
const RANGE_FLOOR_WEEKS = 9;
const RANGE_CAP_WEEKS = 104;

/** Mondays (YYYY-MM-DD) of the first and the last week of a loaded chunk */
interface WeekRange {
	start: string;
	end: string;
}

const currentWeekPlus = (weeks: number) => moment().startOf("isoWeek").add(weeks, "week").format("YYYY-MM-DD");

const navButtonClass =
	"flex h-11 cursor-pointer items-center justify-center rounded-[14px] border border-solid border-[#B5B4B2] !bg-white !px-3 text-sm font-medium text-gray-800 hover:!bg-gray-50";

const SchedulePage = () => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { organisation } = useOrganisation();
	const { customers, isLoading } = useCustomerUsersCustomers();
	const [selectedUnits, setSelectedUnits] = useState<string[]>(customers?.customers?.map((c) => c.id) ?? []);
	// Declined jobs are noise in a planning view, so they're hidden unless asked for
	const [showDeclined, setShowDeclined] = useState(false);
	const scheduleRef = useRef<WeekScheduleHandle>(null);
	// Chunks of weeks in calendar order, added at either end while scrolling
	const [ranges, setRanges] = useState<WeekRange[]>(() => [
		{ start: currentWeekPlus(-RANGE_PAD_BEFORE), end: currentWeekPlus(RANGE_PAD_AFTER) },
	]);

	useEffect(() => {
		setSelectedUnits(customers?.customers?.map((c) => c.id) ?? []);
	}, [JSON.stringify(customers?.customers?.map((c) => c.id))]);

	const scheduleRanges = useMemo(
		() =>
			ranges.map(({ start, end }) => ({
				startTime: moment(start).startOf("day").format(),
				endTime: moment(end).add(6, "day").endOf("day").format(),
			})),
		[ranges],
	);
	const { jobSchedules, loadedRanges, isFetching } = useJobSchedule(
		scheduleRanges,
		selectedUnits,
		["customer", "applications.consultant"],
		["applications.consultant.absences", "applications.consultant.hasWorked"],
	);
	const isLoaded = ranges.map((_, i) => loadedRanges.includes(scheduleRanges[i]));

	// Weeks are shown once their chunk has loaded, so rows never pop in above the viewport
	const loadedWeeks = ranges.filter((_, i) => isLoaded[i]);
	const startWeek = loadedWeeks[0]?.start;
	const endWeek = loadedWeeks[loadedWeeks.length - 1]?.end;
	// Stays true after the first chunk has loaded for the selected units, later
	// unit changes keep the loaded weeks on screen while refetching
	const [isReady, setIsReady] = useState(false);
	useEffect(() => {
		if (!isLoading && startWeek && (selectedUnits.length || !customers?.customers?.length)) setIsReady(true);
	}, [isLoading, startWeek, selectedUnits.length, customers?.customers?.length]);

	const loadEarlierWeeks = () =>
		setRanges((prev) => {
			const floor = currentWeekPlus(-RANGE_FLOOR_WEEKS);
			if (!isLoaded[0] || prev[0].start <= floor) return prev;
			const start = moment.max(moment(prev[0].start).subtract(RANGE_EXTEND_BY, "week"), moment(floor));
			return [{ start: start.format("YYYY-MM-DD"), end: moment(prev[0].start).subtract(1, "week").format("YYYY-MM-DD") }, ...prev];
		});
	const loadLaterWeeks = () =>
		setRanges((prev) => {
			const cap = currentWeekPlus(RANGE_CAP_WEEKS);
			const last = prev[prev.length - 1];
			if (!isLoaded[prev.length - 1] || last.end >= cap) return prev;
			const end = moment.min(moment(last.end).add(RANGE_EXTEND_BY, "week"), moment(cap));
			return [...prev, { start: moment(last.end).add(1, "week").format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") }];
		});

	const allJobs: JobSchedule[] = selectedUnits.length ? jobSchedules : [];
	const declinedCount = allJobs.filter((job) => job.status === "DECLINED").length;
	const jobs = useMemo(
		() => (showDeclined ? allJobs : allJobs.filter((job) => job.status !== "DECLINED")),
		[allJobs, showDeclined],
	);
	const jobIds = useMemo(() => jobs.map((job) => job.id), [jobs]);
	const detailsByJobId = useScheduleJobDetails(jobIds);
	const applicationsByJobId = useScheduleJobApplications(jobIds);
	const competenceLabelByKey = useMemo(
		() => getCompetenceLabelByKey(organisation?.competences ?? []),
		[organisation?.competences],
	);

	const { updateShifts, inviteConsultants, bookApplication, isSaving, isInviting } = useScheduleActions(
		(message) => toast({ title: message, status: "success", duration: 5000, isClosable: true, variant: "subtle" }),
		(errorCode) =>
			toast({
				title: "Det gick inte att spara ändringen",
				description: errorCode,
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			}),
	);

	return (
		<LoggedInLayout width="full" bg={"#edeff3"}>
			<VStack spacing={"1.5rem"} align={"flex-start"} w="full" pb={10}>
				<Flex w="full" justify="space-between" alignItems="center" mt={2}>
					<Text fontWeight={600} fontSize={24} m={0}>
						Schema
					</Text>
					<NewJobButton />
				</Flex>
				{isLoading && <LoadingIndicator size={"s"} />}
				{!isLoading && (
					<HStack flexWrap="wrap" gap={3}>
						{(customers?.customers?.length ?? 0) > 1 && (
							<Popover placement="bottom-start" isLazy>
								<PopoverTrigger>
									<Flex
										height={"44px"}
										width={"280px"}
										borderColor={"#B5B4B2"}
										borderWidth={1}
										borderRadius={14}
										_hover={{ cursor: "pointer" }}
										direction={"row"}
										alignItems={"center"}
										justify={"space-between"}
										backgroundColor={"#FFFFFF"}
										p={2}
									>
										<Text m={0} fontSize="16px" fontWeight={400}>
											Filtrera på enhet
										</Text>
										<DropDownIcon />
									</Flex>
								</PopoverTrigger>
								<PopoverContent>
									<PopoverBody>
										<CheckboxGroup
											colorScheme="blue"
											value={selectedUnits}
											onChange={(value) => {
												setSelectedUnits(value as string[]);
											}}
										>
											<Stack spacing={2} direction={"column"}>
												{customers?.customers?.map((item) => (
													<Checkbox key={item.id} value={item.id}>
														{item.name ?? ""}
													</Checkbox>
												))}
											</Stack>
										</CheckboxGroup>
									</PopoverBody>
								</PopoverContent>
							</Popover>
						)}
						<button
							type="button"
							title="Visa denna vecka"
							className={navButtonClass}
							onClick={() => scheduleRef.current?.scrollToToday()}
						>
							Idag
						</button>
						{(isFetching || isSaving) && <LoadingIndicator size={"s"} />}
						{declinedCount > 0 && (
							<Checkbox
								size="md"
								colorScheme="blue"
								isChecked={showDeclined}
								onChange={(e) => setShowDeclined(e.target.checked)}
								ml={{ md: 2 }}
								// The page background is grey, so the box needs its own white fill and border
								sx={{ ".chakra-checkbox__control": { bg: "white", borderColor: "#B5B4B2" } }}
							>
								<Text as="span" fontSize="sm" color="gray.800">
									Visa nekade uppdrag ({declinedCount})
								</Text>
							</Checkbox>
						)}
					</HStack>
				)}

				{!isLoading && !isReady && <LoadingIndicator size={"s"} />}
				{isReady && startWeek && endWeek && (
					<WeekSchedule
						ref={scheduleRef}
						jobs={jobs}
						detailsByJobId={detailsByJobId}
						applicationsByJobId={applicationsByJobId}
						competenceLabelByKey={competenceLabelByKey}
						startWeek={startWeek}
						endWeek={endWeek}
						onReachStart={loadEarlierWeeks}
						onReachEnd={loadLaterWeeks}
						isLoadingStart={!isLoaded[0]}
						isLoadingEnd={!isLoaded[isLoaded.length - 1]}
						loadConsultants={(jobId) => fetchJobMatchingConsultants(queryClient, jobId)}
						onUpdateShifts={(jobId, message, change) => updateShifts({ jobId, message, change })}
						onInvite={(jobId, consultantIds, allowLaterBooking) =>
							inviteConsultants({ jobId, consultantIds, allowLaterBooking })
						}
						isInviting={isInviting}
						onBook={(jobId, applicationId) => bookApplication({ jobId, applicationId })}
					/>
				)}
			</VStack>
		</LoggedInLayout>
	);
};

export default SchedulePage;
